import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useKeepWarmQueue, type KeepWarmQueueItem } from '@/hooks/useKeepWarmQueue';
import { useMessageTemplates, useCreateTemplate } from '@/hooks/useMessageTemplates';
import { useLogWhatsAppOutbound } from '@/hooks/useWhatsAppActions';
import { LoadingState } from '@/components/LoadingState';
import { SmartCard } from '@/components/ui/smart-card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/ui/page-header';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, Send, Plus, Clock, FileText } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { INACTION_COPY } from '@/lib/inactionCopy';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { TemplatePickerDialog, type LeadLike } from '@/components/keep-warm/TemplatePickerDialog';
import { ManageTemplatesSheet } from '@/components/keep-warm/ManageTemplatesSheet';

/** Extract variable names from body like {first_name} -> ['first_name'] */
function extractVariables(body: string): string[] {
  const matches = body.match(/\{([a-z_]+)\}/gi) ?? [];
  return [...new Set(matches.map((m) => m.slice(1, -1).toLowerCase()))];
}

/** In-page modal to create a message template (inserts into message_templates). */
function CreateTemplateModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const createTemplate = useCreateTemplate();

  const handleSave = async () => {
    if (!name.trim() || !body.trim()) {
      toast.error('Name and body are required');
      return;
    }
    try {
      await createTemplate.mutateAsync({
        name: name.trim(),
        channel: 'whatsapp',
        body: body.trim(),
        category: 'reactivate',
        scope_type: 'keep_warm',
        variables: extractVariables(body),
      });
      toast.success('Template saved');
      setName('');
      setBody('');
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to save template');
    }
  };

  const handleCancel = () => {
    setName('');
    setBody('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md gap-4">
        <DialogHeader>
          <DialogTitle>Create message template</DialogTitle>
          <DialogDescription>
            Add a reusable template for Keep Warm messages. Use {'{first_name}'} for personalization.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="template-name">Name</Label>
            <Input
              id="template-name"
              placeholder="e.g. Re-engage check-in"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-lg"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="template-channel">Channel</Label>
            <Input
              id="template-channel"
              value="WhatsApp"
              readOnly
              disabled
              className="rounded-lg bg-muted/50"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="template-body">Body</Label>
            <Textarea
              id="template-body"
              placeholder="Hi {first_name}, just checking in..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              className="rounded-lg resize-none"
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleCancel} disabled={createTemplate.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={createTemplate.isPending || !name.trim() || !body.trim()}>
            {createTemplate.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Map Keep Warm lead to LeadLike for TemplatePickerDialog */
function toLeadLike(lead: KeepWarmQueueItem['lead']): LeadLike {
  const parts = (lead.name ?? '').trim().split(/\s+/);
  return {
    id: lead.id,
    first_name: parts[0] || lead.name || '',
    last_name: parts.slice(1).join(' ') || '',
    company: '',
    city: '',
    phone: lead.phone ?? undefined,
    whatsapp: lead.phone ?? undefined,
  };
}

export default function KeepWarm() {
  const { data: queue, isLoading: queueLoading } = useKeepWarmQueue(48);
  const { isLoading: templatesLoading } = useMessageTemplates(undefined, { scopeType: 'keep_warm' });
  const logWhatsAppOutbound = useLogWhatsAppOutbound();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [isTemplateModalOpen, setTemplateModalOpen] = useState(false);
  const [activeLead, setActiveLead] = useState<LeadLike | null>(null);

  const isLoading = queueLoading || templatesLoading;

  const onSendWhatsAppClick = (item: KeepWarmQueueItem) => {
    setActiveLead(toLeadLike(item.lead));
    setPickerOpen(true);
  };

  const handleLogOutbound = (payload: {
    leadId: string;
    phone: string;
    message: string;
    templateId?: string;
  }) => {
    logWhatsAppOutbound.mutate(payload);
  };

  // Defensive: Show loading state
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <LoadingState message="Loading keep warm queue..." />
      </div>
    );
  }

  // Empty state with premium skeleton preview
  if (!queue || queue.length === 0) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <PageHeader
          title="Keep Warm queue"
          subtitle="Re-engagement"
          description="Leads waiting for a reply, or cooling after contact, appear here."
        />

        {/* Premium empty state with skeleton preview */}
        <div className="space-y-6">
          <SmartCard className="p-8 border-border/40">
            <div className="text-center space-y-4 max-w-md mx-auto">
              <MessageSquare className="w-12 h-12 text-muted-foreground/40 mx-auto" />
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  When a lead goes quiet for 3+ days, DSOS surfaces it so you re-engage before it goes stale.
                </p>
              </div>
            </div>
          </SmartCard>

          {/* Skeleton preview of a keep-warm card */}
          <SmartCard className="p-6 border-border/40 opacity-60" hover={false}>
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-3 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Skeleton className="h-5 w-32" />
                    <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-400 bg-amber-500/5">
                      KEEP WARM
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="w-3.5 h-3.5" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Skeleton className="h-9 w-32" />
                  <Skeleton className="h-9 w-28" />
                </div>
              </div>
            </div>
          </SmartCard>

          <div className="flex justify-center">
            <Button asChild variant="outline">
              <Link to="/app/leads">View Leads</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeader
          title="Keep Warm queue"
          subtitle="Re-engagement"
          description="Leads waiting for a reply, or cooling after contact, appear here."
        />
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-200"
        >
          Manage templates
        </button>
      </div>

      {/* Queue List */}
      <div className="space-y-4">
        {queue.map((item) => {
          const { lead, keepWarm } = item;
          if (!lead?.id || !lead?.name) return null;

          const lastOutboundDate = keepWarm.lastOutboundAt;

          return (
            <SmartCard key={lead.id} hover className="p-6 border-border/40">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-3 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-foreground truncate">{lead.name}</h3>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] font-semibold uppercase tracking-wide",
                        "border-amber-500/30 text-amber-400 bg-amber-500/5"
                      )}
                    >
                      KEEP WARM
                    </Badge>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[9px] font-semibold uppercase tracking-wide border inline-flex items-center gap-1",
                        keepWarm.state === 'due_now' && "border-red-500/40 text-red-400 bg-red-500/5",
                        keepWarm.state === 'cooling' && "border-amber-500/30 text-amber-400 bg-amber-500/5"
                      )}
                      aria-label={`State: ${keepWarm.state}`}
                    >
                      <span
                        className={cn(
                          "w-1.5 h-1.5 rounded-full",
                          keepWarm.state === 'due_now' && "bg-red-400",
                          keepWarm.state === 'cooling' && "bg-amber-400"
                        )}
                      />
                      {keepWarm.stateLabel}
                    </Badge>
                    {keepWarm.timeSinceOutboundLabel && (
                      <Badge variant="outline" className="text-[10px]">
                        {keepWarm.timeSinceOutboundLabel}
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                    {lastOutboundDate && (
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        <span>
                          {INACTION_COPY.keepWarm.waitingForReply} • {formatDistanceToNow(lastOutboundDate, { addSuffix: true })}
                        </span>
                      </div>
                    )}
                    {keepWarm.state === 'due_now' && keepWarm.overdueByLabel && (
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{INACTION_COPY.keepWarm.overdueBy(keepWarm.overdueByLabel)}</span>
                      </div>
                    )}
                    {keepWarm.state === 'cooling' && keepWarm.dueInLabel && (
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{INACTION_COPY.keepWarm.dueIn(keepWarm.dueInLabel)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => onSendWhatsAppClick(item)}
                    disabled={!lead.phone}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTemplateModalOpen(true)}
                    title="Create template"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSheetOpen(true)}
                    className="text-muted-foreground hover:text-foreground text-xs"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Manage templates
                  </Button>
                </div>
              </div>
            </SmartCard>
          );
        })}
      </div>

      <TemplatePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        lead={activeLead}
        onLogOutbound={handleLogOutbound}
        onManageTemplates={() => {
          setPickerOpen(false);
          setSheetOpen(true);
        }}
      />
      <ManageTemplatesSheet open={sheetOpen} onOpenChange={setSheetOpen} />
      <CreateTemplateModal open={isTemplateModalOpen} onOpenChange={setTemplateModalOpen} />
    </div>
  );
}
